import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { startOfDay, addDays } from 'date-fns'
import { fromZonedTime } from 'date-fns-tz'
import { debounce } from 'lodash'

import { buildCsvFilename, downloadCsv } from '@/utils/file'

import {
  selectDashboardData,
  cancelReservation,
  fetchReservationDashboard,
  getAmenities,
  selectAmenitiesForFilter,
  setRequestStatus,
  deleteTimeBlock,
  selectIsDashboardToggled,
  selectSpacesForFilter,
} from '../../../reducers/Organisation/reservationsReducer'
import { selectUserData } from '../../../reducers/userDataReducer'
import { columns, getStatusCountText } from '../helpers'
import { useSnackbar } from '../../../helpers/hooks/useSnackbar'
import { canIssueRefund } from '../../../helpers/helpers'
import FeaturedTable from '../../FeaturedTable/FeaturedTable'
import PaginationFooter from '../../PaginationFooter/PaginationFooter'
import ConfirmPopup from '../../Popups/ConfirmPopup'
import ReservationsDetailsModal from '../ReservationsDetailsModal/ReservationsDetailsModal'
import ReservationNotesModal from '../ReservationNotesModal/ReservationNotesModal'
import api from '../../../api/api'

import ConfirmPopupBody from './ConfirmPopupBody'
import ReservationsNavbar from './ReservationsNavbar'

const DEFAULT_DAYS_RANGE = 7
const DEFAULT_RANGE = [startOfDay(Date.now()), addDays(startOfDay(Date.now()), DEFAULT_DAYS_RANGE)]

const ReservationsList = ({
  fetchReservationDashboard = () => {},
  getAmenities = () => {},
  setRequestStatus = () => {},
  cancelReservation = () => {},
  deleteTimeBlock = () => {},
  userData,
  reservationsData,
  building,
  amenitiesForFilter,
  spacesForFilter,
  isDashboardToggled,
}) => {
  const { t } = useTranslation(['reservations', 'common'])
  const [isSiteLoading, setSiteLoading] = useState(false)
  const [refundState, setRefundState] = useState('refund')
  const [refundAmount, setRefundAmount] = useState('')
  const [cancelNote, setCancelNote] = useState('')
  const [showOnPage, setShowOnPage] = useState(25)
  const [pageNumber, setPageNumber] = useState(1)
  const [paginationItemsCount, setPaginationItemsCount] = useState(0)
  const [search, setSearch] = useState('')
  const [amenityFilters, setAmenityFilters] = useState([])
  const [selectedTab, setSelectedTab] = useState()
  const [prevSearchValue, setPrevSearchValue] = useState('')
  const [selectedReservation, setSelectedReservation] = useState(null)
  const [canceledReservation, setCanceledReservation] = useState(null)
  const [declinedReservation, setDeclinedReservation] = useState(null)
  const [noteReservation, setNoteReservation] = useState(null)
  const [deletedTimeBlock, setDeletedTimeBlock] = useState(null)
  const [dateRange, setDateRange] = useState(DEFAULT_RANGE)
  const [spaceFilters, setSpaceFilters] = useState([])
  const { displaySnackbar } = useSnackbar()

  const [dateStart, dateEnd] = dateRange
  const isCSVButtonDisabled =
    (selectedTab && selectedTab.value === 'blocked') || !reservationsData.results.length
  const isRefundAllowed = canIssueRefund(
    userData?.user?.role,
    canceledReservation?.date,
    building?.timezone,
  )
  const isDeclineRefundAllowed = canIssueRefund(
    userData?.user?.role,
    declinedReservation?.date,
    building?.timezone,
  )
  const cancelPaidReservationText =
    refundState === 'refund'
      ? t('reservations:reservationsList.yesIssueRefund')
      : refundState === 'partial'
        ? t('reservations:reservationsList.yesIssuePartialRefund')
        : t('reservations:reservationsList.yesCancel')
  const declineReservationText =
    isDeclineRefundAllowed && refundState === 'refund'
      ? t('reservations:reservationsList.yesIssueRefund')
      : t('reservations:reservationsList.yesDeclineReservation')
  const declineFreeReservationText = t('reservations:reservationsList.yesDeclineReservation')
  const isCancelRefundInvalid = refundState === 'partial' && !Number(refundAmount)

  const fetchReservations = async (query, page, limit) => {
    if (query === undefined) {
      query = search
    }

    if (!query && !dateStart && !dateEnd) return

    if (!page) {
      page = pageNumber
    } else {
      setPageNumber(page)
    }

    if (!limit) {
      limit = showOnPage
    } else {
      setShowOnPage(limit)
    }

    setSiteLoading(true)
    const requestData = {
      buildingId: building.id,
      page,
      limit,
      status: (selectedTab && selectedTab.value) || undefined,
      search: query,
      dateStart:
        !query && dateStart ? fromZonedTime(dateStart, building.timezone).getTime() : undefined,
      dateEnd: !query && dateEnd ? fromZonedTime(dateEnd, building.timezone).getTime() : undefined,
      amenityId: amenityFilters.length > 0 ? amenityFilters.map((filter) => filter.id) : undefined,
      locationId: spaceFilters.length > 0 ? spaceFilters.map((filter) => filter.id) : undefined,
    }

    await fetchReservationDashboard(requestData)
      .then((data) => {
        const count = getStatusCountText(selectedTab ? selectedTab.value : '', data)
        setPaginationItemsCount(count)
      })
      .catch((error) => {
        console.error(error.message)
        displaySnackbar(t('reservations:reservationsList.errorFetching'), true)
      })

    setSiteLoading(false)
  }

  const debounceFn = debounce(fetchReservations, 1000)
  const fetchAmenities = async () => {
    try {
      await getAmenities(building.id)
    } catch ({ message }) {
      console.error(message)
    }
  }

  const onApproveReservation = async (reservation) => {
    try {
      setSiteLoading(true)
      await setRequestStatus([reservation.id], 'approve')
      fetchReservations()
      displaySnackbar(t('reservations:reservationsList.approvedSuccessfully'))
    } catch ({ message }) {
      console.error(message)
      displaySnackbar(t('reservations:reservationsList.errorApproving'), true)
    } finally {
      setSiteLoading(false)
    }
  }

  const onDeclineReservation = async () => {
    try {
      setSiteLoading(true)
      await setRequestStatus(
        [declinedReservation.id],
        'decline',
        isDeclineRefundAllowed && refundState === 'refund',
        cancelNote,
      )
      fetchReservations()
      displaySnackbar(t('reservations:reservationsList.declinedSuccessfully'))
    } catch {
      displaySnackbar(t('reservations:reservationsList.errorDeclining'), true)
    } finally {
      setSiteLoading(false)
      setDeclinedReservation(null)
      setCancelNote('')
      setRefundState('refund')
    }
  }

  const onCancelReservation = async () => {
    try {
      setSiteLoading(true)
      await cancelReservation({
        reservationId: canceledReservation.id,
        refund: refundState !== 'no_refund',
        withoutStoreUpdate: true,
        cancelNote,
        ...(refundState === 'partial' && { amount: Number(refundAmount) }),
      })
      fetchReservations()
      displaySnackbar(t('reservations:reservationsList.canceledSuccessfully'))
    } catch {
      displaySnackbar(t('reservations:reservationsList.errorCanceling'), true)
    } finally {
      setSiteLoading(false)
      setCanceledReservation(null)
      setCancelNote('')
      setRefundState('refund')
      setRefundAmount('')
    }
  }

  const onDeleteTimeBlock = async () => {
    try {
      setSiteLoading(true)
      await deleteTimeBlock(deletedTimeBlock.id)
      fetchReservations()
      displaySnackbar(t('reservations:reservationsList.timeBlockCanceledSuccessfully'))
    } catch {
      displaySnackbar(t('reservations:reservationsList.errorDeletingTimeBlock'), true)
    } finally {
      setSiteLoading(false)
      setDeletedTimeBlock(null)
    }
  }

  const onCSVDownload = async () => {
    try {
      const requestData = {
        buildingId: building.id,
        startDate: dateStart ? fromZonedTime(dateStart, building.timezone).getTime() : undefined,
        endDate: dateEnd ? fromZonedTime(dateEnd, building.timezone).getTime() : undefined,
        status: (selectedTab && selectedTab.value) || undefined,
        amenityId:
          amenityFilters.length > 0 ? amenityFilters.map((filter) => filter.id) : undefined,
        locationId: spaceFilters.length > 0 ? spaceFilters.map((filter) => filter.id) : undefined,
        search,
      }

      const { data } = await api.reservationsDownloadCSVReport(requestData)

      downloadCsv({
        blob: data,
        filename: buildCsvFilename({
          building: building?.name || null,
          page: 'reservations',
          dateRange: [dateStart, dateEnd],
          timezone: building?.timezone,
        }),
      })
    } catch ({ message }) {
      console.error(message)
    }
  }

  const onRowClick = ({ original }) => {
    const { status } = original
    if (status !== 'blocked') {
      setSelectedReservation(original)
    }
  }

  useEffect(() => {
    if (building) {
      fetchAmenities()
      setAmenityFilters([])
      setSpaceFilters([])
    }
  }, [building])

  useEffect(() => {
    if (prevSearchValue !== search) {
      window.addEventListener('keydown', debounceFn(search, 1))
    }
    setPrevSearchValue(search)
    return () => {
      window.removeEventListener('keydown', debounceFn)
    }
  }, [search])

  useEffect(() => {
    const dateFilter = (dateStart && dateEnd) || (!dateStart && !dateEnd)
    if (building && dateFilter) {
      fetchReservations(undefined, 1)
    }
  }, [selectedTab, dateStart, dateEnd, amenityFilters, spaceFilters, building])

  useEffect(() => {
    if (!search) {
      setDateRange(DEFAULT_RANGE)
    }
  }, [search])

  useEffect(() => {
    if (isDashboardToggled) {
      fetchReservations()
    }
  }, [isDashboardToggled])

  return (
    <React.Fragment>
      <div className={`reservations-list ${isSiteLoading ? 'is-loading' : ''}`}>
        <ReservationsNavbar
          search={search}
          setSearch={setSearch}
          dateRange={dateRange}
          setDateRange={setDateRange}
          amenityFilters={amenityFilters}
          setAmenityFilters={setAmenityFilters}
          spaceFilters={spaceFilters}
          setSpaceFilters={setSpaceFilters}
          selectedTab={selectedTab}
          setSelectedTab={(tabFilter) => {
            setPageNumber(1)
            setSelectedTab(tabFilter)
          }}
          reservationsData={reservationsData}
          building={building}
          spacesForFilter={spacesForFilter}
          amenitiesForFilter={amenitiesForFilter}
          onCSVDownload={onCSVDownload}
          isCSVButtonDisabled={isCSVButtonDisabled}
        />
        <div className="reservations-list-table">
          {reservationsData.results && reservationsData.results.length > 0 ? (
            <FeaturedTable
              columns={columns(
                {
                  timezone: building.timezone,
                  role: userData.user.role,
                  onShowDetails: setSelectedReservation,
                  onShowNotes: setNoteReservation,
                  onCancelReservation: (reservation) => {
                    setRefundState('refund')
                    setRefundAmount('')
                    setCancelNote('')
                    setCanceledReservation(reservation)
                  },
                  onDecline: setDeclinedReservation,
                  onApprove: onApproveReservation,
                  onDeleteTimeBlock: setDeletedTimeBlock,
                },
                t,
              )}
              data={reservationsData.results}
              onRowClick={onRowClick}
            />
          ) : (
            <div className="error-message">
              {t('reservations:reservationsList.noAvailableReservations')}
            </div>
          )}
        </div>
        {reservationsData.results && reservationsData.results.length > 0 && (
          <PaginationFooter
            showOnPage={showOnPage}
            pageNumber={pageNumber}
            paginationItemsCount={paginationItemsCount}
            setSiteLoading={setSiteLoading}
            setShowOnPage={(count) => fetchReservations(undefined, 1, count)}
            setPageNumber={(page) => fetchReservations(undefined, page)}
          />
        )}
      </div>
      {canceledReservation && (
        <ConfirmPopup
          className="reservations-confirm-popup"
          title={t('reservations:conflictReservations.confirmationTitle')}
          cancelText={t('common:actions.cancel')}
          confirmText={
            canceledReservation.total > 0 ? cancelPaidReservationText : declineFreeReservationText
          }
          confirmDisabled={isCancelRefundInvalid}
          isOpen={!!canceledReservation}
          body={
            <ConfirmPopupBody
              title={
                canceledReservation.total > 0
                  ? t('reservations:reservationsList.confirmCancelReservation')
                  : t('reservations:reservationsList.confirmDeclineReservation')
              }
              shouldRenderRefundOptions={canceledReservation.total > 0}
              allowPartialRefund
              refundBlocked={!isRefundAllowed}
              paidAmount={canceledReservation.total}
              refundState={refundState}
              refundAmount={refundAmount}
              cancelNote={cancelNote}
              onRefundStateChange={(e) => setRefundState(e.target.value)}
              onRefundAmountChange={setRefundAmount}
              onCancelNoteChange={(e) => setCancelNote(e.target.value)}
            />
          }
          cancelCallback={() => setCanceledReservation(null)}
          confirmCallback={onCancelReservation}
        />
      )}
      {declinedReservation && (
        <ConfirmPopup
          className="reservations-confirm-popup"
          title={t('reservations:conflictReservations.confirmationTitle')}
          cancelText={t('common:actions.cancel')}
          confirmText={
            declinedReservation.total > 0 ? declineReservationText : declineFreeReservationText
          }
          isOpen={!!declinedReservation}
          body={
            <ConfirmPopupBody
              title={
                declinedReservation.total > 0
                  ? t('reservations:reservationsList.confirmDenyReservation')
                  : t('reservations:reservationsList.confirmDeclineReservation')
              }
              shouldRenderRefundOptions={declinedReservation.total > 0}
              refundState={refundState}
              cancelNote={cancelNote}
              onRefundStateChange={(e) => setRefundState(e.target.value)}
              onCancelNoteChange={(e) => setCancelNote(e.target.value)}
            />
          }
          cancelCallback={() => setDeclinedReservation(null)}
          confirmCallback={onDeclineReservation}
        />
      )}
      {deletedTimeBlock && (
        <ConfirmPopup
          className="reservations-confirm-popup"
          title={t('reservations:conflictReservations.confirmationTitle')}
          cancelText={t('common:actions.cancel')}
          confirmText={t('reservations:reservationsList.yesDeleteTimeBlock')}
          isOpen={!!deletedTimeBlock}
          body={t('reservations:reservationsList.confirmDeleteTimeBlock')}
          cancelCallback={() => setDeletedTimeBlock(null)}
          confirmCallback={onDeleteTimeBlock}
        />
      )}
      {selectedReservation && (
        <ReservationsDetailsModal
          onClose={() => setSelectedReservation(null)}
          reservationId={selectedReservation.id}
          timezone={building.timezone}
          refreshReservations={() => fetchReservations(undefined, 1)}
        />
      )}
      {noteReservation && (
        <ReservationNotesModal
          onClose={() => setNoteReservation(null)}
          reservationId={noteReservation.id}
          timezone={building.timezone}
          userData={userData}
          isTimeBlock={noteReservation.status === 'blocked'}
        />
      )}
    </React.Fragment>
  )
}

ReservationsList.propTypes = {
  reservationsData: PropTypes.object,
  userData: PropTypes.object,
  building: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  amenities: PropTypes.array,
  isDashboardToggled: PropTypes.bool,
  cancelReservation: PropTypes.func,
  fetchReservationDashboard: PropTypes.func,
  getAmenities: PropTypes.func,
  setRequestStatus: PropTypes.func,
  deleteTimeBlock: PropTypes.func,
}

ReservationsList.displayName = 'ReservationsList'

export default connect(
  (state) => {
    return {
      reservationsData: selectDashboardData(state),
      isDashboardToggled: selectIsDashboardToggled(state),
      userData: selectUserData(state),
      amenitiesForFilter: selectAmenitiesForFilter(state),
      spacesForFilter: selectSpacesForFilter(state),
    }
  },
  {
    cancelReservation,
    fetchReservationDashboard,
    getAmenities,
    setRequestStatus,
    deleteTimeBlock,
  },
)(ReservationsList)
